import type { StaticAutocompleteOption } from '../../technical/inputs/Autocomplete/StaticSingleAutocomplete.props.ts';
import { sortBy, uniq } from 'lodash/fp';

export const HEIGHT_PX = 24;

export interface SubfundAutocompleteOptionInput {
  name: string;
  id: number;
  dimension: string;
}

const subFundSorting = (subFund: SubfundAutocompleteOptionInput) => [subFund.dimension ?? '', subFund.name];

export const createSubfundAutocompleteOptions = <T extends SubfundAutocompleteOptionInput>(
  subFunds: T[]
): {
  options: StaticAutocompleteOption<T>[];
  optionHeight: number;
  limitTags: number;
  groupBy: (option: StaticAutocompleteOption<T | null>) => string;
  isValueEqual: (a: T | undefined | null, b: T | undefined | null) => boolean;
} => {
  return {
    limitTags: 3,
    optionHeight: HEIGHT_PX,
    groupBy: (option: StaticAutocompleteOption<T | null>): string => option.value?.dimension ?? '',
    options: sortBy(subFundSorting, subFunds).map((subFund) => ({
      searchText: subFund.name,
      value: subFund,
      label: subFund.name,
      key: subFund.name,
    })),
    isValueEqual: (a: T | undefined | null, b: T | undefined | null): boolean => (!a && !b) || a?.id === b?.id,
  };
};

export const createSubfundIdAutocompleteOptions = (
  subFunds: SubfundAutocompleteOptionInput[]
): {
  options: StaticAutocompleteOption<number>[];
  optionHeight: number;
  limitTags: number;
  groupBy: (option: StaticAutocompleteOption<number | null>) => string;
  isValueEqual: (a: number | undefined | null, b: number | undefined | null) => boolean;
} => {
  const idToSubfund = new Map(subFunds.map((subfund) => [subfund.id, subfund]));
  return {
    ...createSubfundAutocompleteOptions(subFunds),
    groupBy: (option: StaticAutocompleteOption<number | null>): string => {
      const subfund = idToSubfund.get(option.value ?? -1);
      if (subfund) {
        return subfund.dimension;
      }

      return '';
    },
    options: sortBy(subFundSorting, subFunds).map((subFund) => ({
      searchText: subFund.name,
      value: subFund.id,
      label: subFund.name,
      key: subFund.name,
    })),
    isValueEqual: (a: number | undefined | null, b: number | undefined | null): boolean => (!a && !b) || a === b,
  };
};

export const calculateAllSubAccountIds = (subFund: {
  subAccounts: {
    id: string;
  }[];
  accounts: {
    id: string;
    subAccounts: {
      id: string;
    }[];
  }[];
}): string[] => {
  return uniq([
    ...subFund.subAccounts.map((subAccount) => subAccount.id),
    ...subFund.accounts.flatMap((account) => account.subAccounts.map((subAccount) => subAccount.id)),
  ]);
};

export const createDimensionAutocompleteOptions = (
  dimensions: string[]
): {
  options: StaticAutocompleteOption<string>[];
  optionHeight: number;
  limitTags: number;
  groupBy: (option: StaticAutocompleteOption<string | null>) => string;
  isValueEqual: (a: string | undefined | null, b: string | undefined | null) => boolean;
} => {
  const sortedDimensions = sortBy((dim) => dim.toLowerCase(), uniq(dimensions));
  return {
    limitTags: 3,
    optionHeight: HEIGHT_PX,
    groupBy: (option: StaticAutocompleteOption<string | null>): string => option.value ?? '',
    options: sortedDimensions.map((dim) => ({
      searchText: dim,
      value: dim,
      label: dim,
      key: dim,
    })),
    isValueEqual: (a: string | undefined | null, b: string | undefined | null): boolean => (!a && !b) || a === b,
  };
};

export const createDimensionAutocompleteOptionsFromSubFunds = (
  subFunds: { dimension: string }[]
): {
  options: StaticAutocompleteOption<string>[];
  optionHeight: number;
  limitTags: number;
  groupBy: (option: StaticAutocompleteOption<string | null>) => string;
  isValueEqual: (a: string | undefined | null, b: string | undefined | null) => boolean;
} => {
  return createDimensionAutocompleteOptions(subFunds.map((subFund) => subFund.dimension));
};
